import { CompleteAccountModalContext, ModalContext, openPlaceAbsenteeBidModal } from '@/redux/modules/modal';
import { EventContext } from '@/redux/modules/analytics';
import { getBidderId } from '@/redux/modules/account/user/user.selectors';
import { getBidIncrementsByItemId } from '@/redux/modules/bidIncrements/bidIncrements.selectors';
import { getBidLimitBalance, getTotalBidLimitBalance } from '@/redux/modules/bidLimit';
import { getCatalogCurrency, getCatalogIsInstantAutopay } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getCurrentTimestamp, getLiveItemDataCurrentAskPrice } from '@/redux/modules/console';
import {
    getItemModelCatalogId,
    getItemModelStartPrice,
    getItemSummaryBidderMaxBid,
    getItemSummaryLeadingBid,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getLiveItemStatusOpen } from '@/redux/modules/liveItemStatus';
import { getNextBidMinimum } from '@/utils/absenteeBids';
import { submitLiveBid } from '@/redux/modules/liveBid';
import { temporaryBidderMaxBidAtom } from '@/components/Modals/AuthModal/AbsenteeBid/pages/AbsenteeBid/components/PlaceBidForm/components/SelectBidDropdown/SelectBidDropdown';
import { TrackPlaceBidPressedButtonType, useTrackPlaceBidPressedAnalytics } from '@/utils/amplitude/analytics';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useSetAtom } from 'jotai';
import usePrevious from '@liveauctioneers/caterwaul-components/lib/hooks/usePrevious';

type Props = {
    itemId: number;
};

export const usePlaceBid = ({ itemId }: Props) => {
    const dispatch = useAppDispatch();
    const location = useLocation();

    const bidderMaxBid = useAppSelector((state) => getItemSummaryBidderMaxBid(state, itemId)) || 0;
    const catalogId = useAppSelector((state) => getItemModelCatalogId(state, itemId));
    const leadingBid = useAppSelector((state) => getItemSummaryLeadingBid(state, itemId)) || 0;
    const startPrice = useAppSelector((state) => getItemModelStartPrice(state, itemId)) || 0;

    const bidIncrements = useAppSelector((state) => getBidIncrementsByItemId(state, itemId));

    // need to be sure to set the bidAmount initially
    const [bidAmount, setBidAmount] = useState(
        getNextBidMinimum(bidIncrements, Math.max(bidderMaxBid, leadingBid), startPrice) || 0
    );
    const isInstantAutopay = useAppSelector((state) => getCatalogIsInstantAutopay(state, catalogId));
    const isLotOpen = useAppSelector((state) => getLiveItemStatusOpen(state, itemId));
    const currentAskPrice = useAppSelector((state) => getLiveItemDataCurrentAskPrice(state, itemId));
    const currency = useAppSelector((state) => getCatalogCurrency(state, catalogId));

    const bidderId = useAppSelector(getBidderId);
    const [stateItemId, setItemId] = useState(itemId || 0);
    const [newBidMinimum, setNewBidMinimum] = useState(0);
    const setTemporaryBidderMaxBid = useSetAtom(temporaryBidderMaxBidAtom);
    const totalCatalogBidLimitBalance = useAppSelector((state) => getTotalBidLimitBalance(state, catalogId));
    const bidLimitBalance = useAppSelector((state) => getBidLimitBalance(state, catalogId));
    const previousBidLimitBalance = usePrevious(bidLimitBalance);
    const previousItemId = usePrevious(stateItemId);
    const previousNewBidMinimum = usePrevious(newBidMinimum);

    const trackPlaceBidPressed = useTrackPlaceBidPressedAnalytics(catalogId, itemId);

    // If the bid (or its environment) changes, update state values
    useEffect(() => {
        // if there is no bid limit then `To Infinity! and Beyond!`
        const balance = bidLimitBalance || Infinity;
        // We need to store the lowest bid option so that we can update the state when
        // the bid options change

        const newBidMinimum = getNextBidMinimum(bidIncrements, Math.max(bidderMaxBid, leadingBid), startPrice) || 0;

        if (
            previousBidLimitBalance !== balance ||
            previousItemId !== itemId ||
            previousNewBidMinimum !== newBidMinimum
        ) {
            setBidAmount(newBidMinimum);
            setItemId(itemId);
            setNewBidMinimum(newBidMinimum);
        }
    }, [
        bidIncrements,
        bidLimitBalance,
        bidderMaxBid,
        stateItemId,
        leadingBid,
        previousBidLimitBalance,
        previousItemId,
        previousNewBidMinimum,
        startPrice,
        itemId,
        totalCatalogBidLimitBalance,
        bidderId,
    ]);

    return useCallback(
        (buttonType?: TrackPlaceBidPressedButtonType) => {
            trackPlaceBidPressed(buttonType);

            // LOT IS OPEN FOR LIVE BIDDING
            if (isLotOpen) {
                dispatch(submitLiveBid(getCurrentTimestamp(), catalogId, currentAskPrice, currency, itemId, location));
            } else {
                const modalContext: ModalContext = {
                    eventContext: EventContext.placeBid,
                    id: itemId,
                    itemId,
                    // When the user logs in, we need to set the temporary bidder max bid to keep it after login refresh
                    runAtLoginArgs: {
                        bidAmount,
                    },
                    runAtLoginFunction: () => {
                        setTemporaryBidderMaxBid(bidAmount);
                    },
                    type: CompleteAccountModalContext.PlaceBid,
                };

                dispatch(
                    openPlaceAbsenteeBidModal({
                        bidAmount,
                        isInstantAutopay,
                        itemId,
                        modalContext,
                    })
                );
            }
        },
        [
            bidAmount,
            catalogId,
            currency,
            currentAskPrice,
            dispatch,
            isInstantAutopay,
            isLotOpen,
            itemId,
            location,
            trackPlaceBidPressed,
            setTemporaryBidderMaxBid,
        ]
    );
};
